<script setup lang="ts">
import { useFieldControlId } from './fieldContext';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { ComboboxRateLimited, type ComboboxLabels, type ComboboxOption } from './combobox';
import LumiIcon from '../icons/LumiIcon.vue';

// The suite's first server-searching combobox (D13, issue 57). Built for the
// place picker, but it knows nothing about places, HTTP or i18n:
//
//  - `search` is supplied by the consumer and is the ONLY way rows arrive, so
//    the app keeps its own API client (headers, auth, active role) and this file
//    stays a dumb control like the rest of forms/.
//  - every string it can render comes in through `labels`, so the consumer owns
//    the nine locales.
//
// The defining behaviour is that a typed value is NEVER lost. This control sits
// in front of a dataset that cannot possibly contain every name a customer
// needs, so "no match" is an ordinary outcome that keeps the text, not an error
// that blocks the form. Selecting a row is an ADDITION to the typed text, and
// clearing the selection leaves the text alone.
//
// LOCAL MODE (#483): pass `items` instead of `search` when the whole list is
// already in hand (an e-mail template picker, say). Then there is nothing to wait
// for, so there is no debounce and no minimum length: focusing or clicking the
// input opens the full list, typing filters it by label and hint, and a field
// that still shows its picked row opens onto the whole list again rather than
// onto that one row — picking something else is the reason to open it.

const props = withDefaults(
  defineProps<{
    /** Runs the server search. Rejecting with ComboboxRateLimited pauses instead of failing. */
    search?: (query: string, signal: AbortSignal) => Promise<ComboboxOption[]>;
    /** Local mode: the complete list, filtered in place. Takes precedence over `search`. */
    items?: ComboboxOption[];
    labels: ComboboxLabels;
    /** Below this many characters nothing is searched — the server ignores them anyway. */
    minQueryLength?: number;
    debounceMs?: number;
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    /** Id of the text input; `${id}-listbox` and `${id}-opt-N` are derived from it. */
    id?: string;
  }>(),
  {
    search: undefined,
    items: undefined,
    minQueryLength: 2,
    debounceMs: 250,
    disabled: false,
    invalid: false,
  },
);

const isLocal = computed(() => Array.isArray(props.items));

// Two models, deliberately independent. `query` is what the customer typed and
// survives every outcome; `modelValue` is the row they picked, or null when they
// picked nothing. A parent that only cares about the text can ignore the second.
const selected = defineModel<ComboboxOption | null>({ default: null });
const query = defineModel<string>('query', { default: '' });

const emit = defineEmits<{
  (e: 'select', option: ComboboxOption): void;
  (e: 'clear'): void;
}>();

type Status = 'idle' | 'typeMore' | 'searching' | 'results' | 'noMatch' | 'error' | 'rateLimited';

const open = ref(false);
const status = ref<Status>('idle');
const options = ref<ComboboxOption[]>([]);
const activeIndex = ref(-1);
const rootEl = ref<HTMLElement | null>(null);
const listEl = ref<HTMLElement | null>(null);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: AbortController | null = null;
// Monotonic request id: a slow first response must never overwrite a fast later
// one. Every keystroke can be in flight at once on a bad connection.
let requestSeq = 0;

// The enclosing Field's id when the consumer passed none; the fixed fallback keeps a bare
// control working (and its listbox ids derived) outside a Field.
const fieldId = useFieldControlId(() => props.id);
const inputId = computed(() => fieldId.value ?? 'lumi-combobox');
const listboxId = computed(() => `${inputId.value}-listbox`);
const activeOptionId = computed(() =>
  activeIndex.value >= 0 ? `${inputId.value}-opt-${activeIndex.value}` : undefined,
);

// What the panel says when it is not showing rows. Kept as one computed so the
// panel and the live region can never disagree about the state.
const statusText = computed(() => {
  switch (status.value) {
    case 'searching':
      return props.labels.searching;
    case 'typeMore':
      return props.labels.typeMore;
    case 'noMatch':
      return props.labels.noMatch;
    case 'error':
      return props.labels.error;
    case 'rateLimited':
      return props.labels.rateLimited;
    default:
      return '';
  }
});

// Announced politely, so a screen-reader user learns the result count without
// the list stealing focus (the ARIA combobox pattern keeps focus in the input).
const announcement = computed(() => {
  if (status.value === 'results') {
    return props.labels.resultsAnnouncement(options.value.length);
  }
  return statusText.value;
});

const showPanel = computed(() => open.value && (options.value.length > 0 || statusText.value !== ''));

// Where the panel opens. Below by default; above when the space under the
// control — inside the viewport AND inside the nearest scrolling ancestor,
// which is what clips an absolutely positioned panel — cannot hold it and the
// space above can. A control at the foot of a scroll region would otherwise
// open into a list the reader cannot scroll to.
const PANEL_MAX_HEIGHT_PX = 264 + 4;
const placement = ref<'below' | 'above'>('below');

function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

function updatePlacement() {
  const root = rootEl.value;
  if (!root || typeof window === 'undefined') return;
  const rect = root.getBoundingClientRect();
  const clip = scrollParentOf(root)?.getBoundingClientRect();
  const bottomEdge = Math.min(window.innerHeight, clip?.bottom ?? Number.POSITIVE_INFINITY);
  const topEdge = Math.max(0, clip?.top ?? 0);
  const spaceBelow = bottomEdge - rect.bottom;
  const spaceAbove = rect.top - topEdge;
  placement.value = spaceBelow < PANEL_MAX_HEIGHT_PX && spaceAbove > spaceBelow ? 'above' : 'below';
}

function watchPlacement(on: boolean) {
  if (typeof window === 'undefined') return;
  const method = on ? 'addEventListener' : 'removeEventListener';
  window[method]('scroll', updatePlacement, true);
  window[method]('resize', updatePlacement);
}

watch(showPanel, async (visible) => {
  watchPlacement(visible);
  if (!visible) return;
  await nextTick();
  updatePlacement();
});

function clearTimers() {
  if (debounceTimer) clearTimeout(debounceTimer);
  if (retryTimer) clearTimeout(retryTimer);
  debounceTimer = null;
  retryTimer = null;
}

function abortInFlight() {
  if (!inFlight) return;
  inFlight.abort();
  inFlight = null;
  // Bump the sequence too: an aborted request's rejection must be ignored, not
  // rendered. Without this, closing the control mid-search leaves the error
  // state armed and the next focus opens onto "search failed".
  requestSeq++;
}

async function runSearch(term: string) {
  if (!props.search) return;
  abortInFlight();
  const controller = new AbortController();
  inFlight = controller;
  const seq = ++requestSeq;
  status.value = 'searching';
  try {
    const rows = await props.search!(term, controller.signal);
    if (seq !== requestSeq) return;
    options.value = Array.isArray(rows) ? rows : [];
    status.value = options.value.length ? 'results' : 'noMatch';
    activeIndex.value = -1;
  } catch (err: unknown) {
    if (seq !== requestSeq) return;
    options.value = [];
    activeIndex.value = -1;
    if (err instanceof ComboboxRateLimited) {
      // Wait the server's own window out and try the SAME term again, rather
      // than telling the user their search failed.
      status.value = 'rateLimited';
      const waitMs = Math.max(1, Math.ceil(err.retryAfterSeconds)) * 1000;
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (query.value.trim() === term) void runSearch(term);
      }, waitMs);
      return;
    }
    status.value = 'error';
  } finally {
    if (inFlight === controller) inFlight = null;
  }
}

// `typed` is passed by onInput. It has to be: when the consumer binds
// `v-model:query`, Vue does NOT update this component's copy of the model
// synchronously — the new text only arrives on the parent's next render. Reading
// `query.value` here would therefore search the PREVIOUS keystroke, and the
// empty string on the first one.
function scheduleSearch(typed?: string) {
  clearTimers();
  const term = (typed ?? query.value).trim();
  if (isLocal.value) {
    filterLocal(term);
    return;
  }
  if (term.length < props.minQueryLength) {
    abortInFlight();
    // Bump the sequence so a response for a longer, now-deleted term cannot land
    // on top of this state.
    requestSeq++;
    options.value = [];
    activeIndex.value = -1;
    status.value = term.length ? 'typeMore' : 'idle';
    return;
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void runSearch(term);
  }, props.debounceMs);
}

/** Local mode: the rows are the items whose label or hint contains the term. */
function filterLocal(term: string) {
  const needle = term.toLocaleLowerCase();
  const items = props.items ?? [];
  options.value = needle
    ? items.filter(
        (item) =>
          item.label.toLocaleLowerCase().includes(needle) ||
          (item.hint ?? '').toLocaleLowerCase().includes(needle),
      )
    : items.slice();
  status.value = options.value.length ? 'results' : 'noMatch';
  activeIndex.value = -1;
}

/**
 * What a local list opens onto: the typed text, unless the text is just the
 * picked row's own label — then the whole list, because someone opening a filled
 * picker wants to change the pick, not to be shown the row they already have.
 */
function browseTerm(): string {
  return selected.value && query.value === selected.value.label ? '' : query.value;
}

function openLocal() {
  if (!isLocal.value || props.disabled || open.value) return;
  open.value = true;
  filterLocal(browseTerm().trim());
}

function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  query.value = value;
  // The text no longer describes the picked row, so the pick is gone. The text
  // stays: that is the whole point of this control.
  if (selected.value) {
    selected.value = null;
    emit('clear');
  }
  open.value = true;
  scheduleSearch(value);
}

function choose(index: number) {
  const option = options.value[index];
  if (!option) return;
  selected.value = option;
  query.value = option.label;
  emit('select', option);
  close();
}

function clearSelection() {
  if (!selected.value) return;
  selected.value = null;
  emit('clear');
}

function close() {
  open.value = false;
  activeIndex.value = -1;
  clearTimers();
  abortInFlight();
}

async function moveActive(delta: number) {
  if (!open.value) {
    if (isLocal.value) {
      openLocal();
      return;
    }
    open.value = true;
    if (!options.value.length) scheduleSearch();
    return;
  }
  if (!options.value.length) return;
  const next = activeIndex.value + delta;
  activeIndex.value = next < 0 ? options.value.length - 1 : next >= options.value.length ? 0 : next;
  await nextTick();
  listEl.value?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView?.({ block: 'nearest' });
}

function onKeydown(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      void moveActive(1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      void moveActive(-1);
      break;
    case 'Home':
      if (open.value && options.value.length) {
        event.preventDefault();
        activeIndex.value = 0;
      }
      break;
    case 'End':
      if (open.value && options.value.length) {
        event.preventDefault();
        activeIndex.value = options.value.length - 1;
      }
      break;
    case 'Enter':
      // Only swallow Enter when it actually picks something; otherwise the form's
      // own submit must still work.
      if (open.value && activeIndex.value >= 0) {
        event.preventDefault();
        choose(activeIndex.value);
      }
      break;
    case 'Escape':
      if (open.value) {
        event.preventDefault();
        close();
      }
      break;
    case 'Tab':
      close();
      break;
    default:
      break;
  }
}

function onFocusout(event: FocusEvent) {
  const next = event.relatedTarget as Node | null;
  if (next && rootEl.value?.contains(next)) return;
  close();
}

function onFocus() {
  if (isLocal.value) {
    openLocal();
    return;
  }
  if (options.value.length || statusText.value) open.value = true;
}

// A local list that arrives (or changes) while the panel is open re-filters in
// place, so a picker opened before its items loaded does not sit on "no match".
watch(
  () => props.items,
  () => {
    if (isLocal.value && open.value) filterLocal(browseTerm().trim());
  },
);

// A parent that rewrites `query` (loading an existing record, resetting the
// form) must not leave a stale panel behind. Only the picked row's own label is
// exempt — with nothing picked, every rewrite counts, clearing the text
// included: a list-builder that empties the field after each entry would
// otherwise reopen onto the previous entry's results on the next focus.
watch(
  () => query.value,
  (value) => {
    if (!open.value && (!selected.value || value !== selected.value.label)) {
      options.value = [];
      status.value = 'idle';
    }
  },
);

onBeforeUnmount(() => {
  watchPlacement(false);
  clearTimers();
  abortInFlight();
});

defineExpose({ close });
</script>

<template>
  <div ref="rootEl" class="lumi-combobox" @focusout="onFocusout">
    <div class="lumi-combobox__control">
      <input
        :id="inputId"
        class="lumi-combobox__input"
        :class="{ 'lumi-combobox__input--invalid': invalid }"
        type="text"
        role="combobox"
        autocomplete="off"
        :value="query"
        :placeholder="placeholder"
        :disabled="disabled"
        :aria-expanded="showPanel"
        :aria-controls="listboxId"
        aria-autocomplete="list"
        :aria-activedescendant="activeOptionId"
        @input="onInput"
        @keydown="onKeydown"
        @focus="onFocus"
        @click="openLocal"
      />
      <button
        v-if="selected && !disabled"
        type="button"
        class="lumi-combobox__clear"
        :aria-label="labels.clear"
        @click="clearSelection"
      >
        <LumiIcon name="remove" width="14" height="14" stroke-width="2.5" />
      </button>
    </div>

    <!-- The popover layer. 60/61 is this library's established popover-over-content
         pair (ModuleBadge, Sidebar); run two (overlays-drafts) binds it to a real
         popover layer token, so nothing new is invented here. Unlike those menus
         this panel carries NO click-catching backdrop: a combobox is a form
         control, and a backdrop would swallow the click that moves focus to the
         next field. Focus leaving the root closes it instead. -->
    <div
      v-if="showPanel"
      :id="listboxId"
      ref="listEl"
      class="lumi-combobox__panel"
      :class="{ 'lumi-combobox__panel--above': placement === 'above' }"
      role="listbox"
    >
      <template v-if="options.length">
        <div
          v-for="(option, index) in options"
          :id="`${inputId}-opt-${index}`"
          :key="option.value"
          class="lumi-combobox__option"
          :class="{ 'lumi-combobox__option--active': index === activeIndex }"
          :data-active="index === activeIndex"
          role="option"
          :aria-selected="selected?.value === option.value"
          @mousedown.prevent="choose(index)"
          @mousemove="activeIndex = index"
        >
          <span class="lumi-combobox__label">{{ option.label }}</span>
          <span v-if="option.hint" class="lumi-combobox__hint">{{ option.hint }}</span>
        </div>
      </template>
      <div v-else class="lumi-combobox__status" :class="{ 'lumi-combobox__status--error': status === 'error' }">
        <span>{{ statusText }}</span>
        <button
          v-if="status === 'error'"
          type="button"
          class="lumi-combobox__retry"
          @mousedown.prevent="scheduleSearch()"
        >
          {{ labels.retry }}
        </button>
      </div>
    </div>

    <span class="lumi-combobox__sr" role="status" aria-live="polite">{{ announcement }}</span>
  </div>
</template>

<style>
.lumi-combobox{position:relative;display:block;font-family:var(--font-body);}
.lumi-combobox__control{position:relative;display:block;}
.lumi-combobox__input{display:block;width:100%;height:var(--field-h);padding:0 12px;border:1px solid var(--field-border);border-radius:var(--radius-md);background:var(--field-bg);color:var(--text-heading);font-family:var(--font-body);font-size:var(--text-sm);line-height:1.5;transition:border-color var(--dur-fast);padding-right:34px;}
.lumi-combobox__input::placeholder{color:var(--text-tertiary);}
.lumi-combobox__input:focus{border-color:var(--panel-edge);}
.lumi-combobox__input:disabled{background:var(--surface-wash);color:var(--text-secondary);cursor:not-allowed;}
.lumi-combobox__input--invalid{border-color:var(--danger);}
.lumi-combobox__input--invalid:focus{border-color:var(--danger);}
.lumi-combobox__input--invalid:focus-visible{outline-color:var(--danger);}
.lumi-combobox__clear{position:absolute;right:8px;top:50%;transform:translateY(-50%);display:flex;align-items:center;justify-content:center;width:24px;height:24px;padding:0;border:none;border-radius:var(--radius-sm);background:transparent;color:var(--text-secondary);cursor:pointer;}
.lumi-combobox__clear:hover{background:var(--surface-wash);color:var(--text-heading);}
.lumi-combobox__panel{position:absolute;z-index:calc(var(--layer-popover) + 1);top:calc(100% + 4px);left:0;right:0;max-height:264px;overflow-y:auto;background:var(--surface-card);border:1px solid var(--panel-edge);border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);padding:var(--space-1);}
.lumi-combobox__panel--above{top:auto;bottom:calc(100% + 4px);}
.lumi-combobox__option{display:flex;flex-direction:column;gap:2px;padding:6px 10px;border-radius:var(--radius-md);cursor:pointer;}
.lumi-combobox__option--active{background:var(--selected-bg);}
.lumi-combobox__label{font-size:var(--text-sm);color:var(--text-heading);}
.lumi-combobox__hint{font-size:var(--text-xs);color:var(--text-secondary);}
.lumi-combobox__status{display:flex;align-items:center;gap:var(--space-2);padding:8px 10px;font-size:var(--text-xs);color:var(--text-secondary);}
.lumi-combobox__status--error{color:var(--danger-text);}
.lumi-combobox__retry{padding:0;border:none;background:transparent;color:var(--text-heading);font-family:var(--font-body);font-size:var(--text-xs);font-weight:var(--weight-medium);text-decoration:underline;cursor:pointer;}
.lumi-combobox__sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip-path:inset(50%);white-space:nowrap;border:0;}
</style>
